import type { Block } from "./blocks";
import type { TemplateDefaults } from "./defaults";
import type { PopupEmbedSettings } from "./popup-embed";

/**
 * One logical screen/step (e.g. popup wizard step) in the editor. When
 * {@link TemplateContent.canvasPages} is present, {@link TemplateContent.blocks}
 * is kept as an alias of the active page's {@link CanvasPage.blocks} array so all
 * existing block APIs operate on the current step.
 */
export interface CanvasPage {
  id: string;
  title: string;
  blocks: Block[];
}

export interface TemplateSettings {
  width: number;
  backgroundColor: string;
  /**
   * Optional alpha applied on top of {@link TemplateSettings.backgroundColor}
   * for the popup card / canvas background in the editor preview. `0` is
   * fully transparent and `1` is fully opaque. When omitted (or `undefined`),
   * consumers should treat it as `1` for backwards compatibility.
   */
  backgroundOpacity?: number;
  /**
   * Optional drop-shadow intensity for the popup card / canvas card in the
   * editor preview, on a 0..1 scale. `0` removes the shadow entirely; `1`
   * paints the strongest soft shadow. When omitted (or `undefined`),
   * consumers should fall back to the existing default shadow so legacy
   * templates render unchanged.
   */
  backgroundShadow?: number;
  fontFamily: string;
  preheaderText?: string;
  /**
   * BCP-47 language code for the rendered email's `<html lang>`. Drives
   * screen-reader pronunciation. Default `'en'` via `DEFAULT_TEMPLATE_DEFAULTS`.
   */
  locale: string;
  /**
   * Website popup chrome (triggers, overlay layout). Present when using the popup editor.
   */
  popup?: PopupEmbedSettings;
}

export interface TemplateContent {
  blocks: Block[];
  settings: TemplateSettings;
  /** Present when editing multi-step / multi-popup canvases. See {@link CanvasPage}. */
  canvasPages?: CanvasPage[];
  /** Must refer to an entry in `canvasPages` when `canvasPages` is non-empty. */
  activeCanvasPageId?: string;
}

/**
 * When {@link TemplateContent.canvasPages} is set, ensures `blocks` references the
 * active page's `blocks` array (same reference). Call after loading persisted JSON
 * so edits mutate the correct page.
 */
export function normalizeTemplateContentPages(
  c: TemplateContent,
): TemplateContent {
  if (!c.canvasPages?.length) {
    return c;
  }
  const activeId = c.activeCanvasPageId ?? c.canvasPages[0].id;
  const page = c.canvasPages.find((p) => p.id === activeId) ?? c.canvasPages[0];
  return {
    ...c,
    activeCanvasPageId: page.id,
    blocks: page.blocks,
  };
}

export function createDefaultTemplateContent(
  _defaultFontFamily = "Arial",
  _templateDefaults?: TemplateDefaults,
): TemplateContent {
  return {
    blocks: [
      {
        id: "dcf862dc-2ff6-45e1-8f43-07ebc2d5a88f",
        type: "section",
        styles: {
          margin: {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          },
          padding: {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          },
        },
        columns: "2",
        children: [
          [
            {
              id: "b1353687-5d54-4c8c-8806-bf0a77de7aad",
              url: "wwww.google.com",
              text: "GET 20% OFF",
              type: "button",
              styles: {
                margin: {
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                },
                padding: {
                  top: 5,
                  left: 0,
                  right: 0,
                  bottom: 0,
                },
              },
              fontSize: 13,
              textColor: "#000000",
              borderColor: "#FFCB54",
              borderSides: "all",
              borderWidth: 4,
              borderRadius: 39,
              buttonPadding: {
                top: 12,
                left: 24,
                right: 24,
                bottom: 12,
              },
              backgroundColor: "#ffffff",
            },
            {
              id: "035f1574-3aff-4741-84cc-2d4b6e0d2fde",
              type: "paragraph",
              styles: {
                margin: {
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                },
                padding: {
                  top: 10,
                  left: 10,
                  right: 10,
                  bottom: 10,
                },
              },
              content:
                '<p style="text-align: center;"><span style="color: rgb(27, 27, 32); font-family: Poppins; font-size: 20px; letter-spacing: normal;"><strong>You forgot something in your cart!</strong></span></p>',
            },
            {
              id: "866f0ac7-b3df-4c62-b595-7d6adb975550",
              type: "paragraph",
              styles: {
                margin: {
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                },
                padding: {
                  top: 10,
                  left: 10,
                  right: 10,
                  bottom: 10,
                },
              },
              content:
                '<p style="text-align: center; line-height: 1;"><span style="color: inherit; font-family: inherit; font-size: inherit; letter-spacing: normal;">Before you go, fill out your email for a </span></p><p style="text-align: center; line-height: 1;"><span style="color: inherit; font-family: inherit; font-size: inherit; letter-spacing: normal;"><strong>20% off code</strong> to use at checkout.</span></p>',
            },
            {
              id: "27bab77f-14ac-4007-b4f8-14c8398b581c",
              name: "Email",
              type: "input",
              label: "",
              styles: {
                margin: {
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                },
                padding: {
                  top: 0,
                  left: 10,
                  right: 10,
                  bottom: 0,
                },
              },
              required: true,
              inputType: "text",
              inputWidth: "full",
              labelColor: "#374151",
              inputMargin: {
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              },
              labelMargin: {
                top: 0,
                left: 0,
                right: 0,
                bottom: 8,
              },
              placeholder: "email@website.com",
              defaultValue: "",
              inputPadding: {
                top: 10,
                left: 12,
                right: 12,
                bottom: 10,
              },
              labelPadding: {
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              },
              inputFontSize: 15,
              labelFontSize: 14,
              inputTextColor: "#111827",
              labelTextAlign: "left",
              labelFontWeight: "normal",
              inputBorderColor: "#d1d5db",
              inputBorderSides: "all",
              inputBorderWidth: 1,
              inputBorderRadius: 6,
              inputBackgroundColor: "#ffffff",
              inputPlaceholderColor: "#9ca3af",
            },
            {
              id: "046d68dd-e631-45a3-b729-a1c3276c08fc",
              url: "",
              text: "Get coupon",
              type: "button",
              styles: {
                margin: {
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                },
                padding: {
                  top: 6,
                  left: 0,
                  right: 0,
                  bottom: 3,
                },
              },
              fontSize: 15,
              textColor: "#ffffff",
              borderColor: "#000000",
              borderSides: "all",
              borderWidth: 0,
              borderRadius: 30,
              buttonPadding: {
                top: 12,
                left: 24,
                right: 24,
                bottom: 12,
              },
              backgroundColor: "#f5ba25",
            },
          ],
          [
            {
              id: "36fef265-889e-4417-b29d-a275bf9b7483",
              alt: "",
              src: "https://cdn.wisepops.net/cdn-cgi/image/fit=scale-down,format=auto,width=1724/images-secure/popups/w.ZniQi6KSVq/01684a642ed5f99d268f97c9fc6a4338.png",
              type: "image",
              align: "center",
              width: "full",
              styles: {
                margin: {
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                },
                padding: {
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                },
              },
              visibility: {
                mobile: false,
                tablet: true,
                desktop: true,
              },
            },
          ],
        ],
      },
    ],
    settings: {
      popup: {
        design: {
          rtl: false,
          sizePreset: "large",
          useOverlay: true,
          overlayImage: false,
          popupPosition: "center",
          contentPadding: {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          },
          backgroundImage: false,
          displayAnimation: "smooth",
          multiColumnPreset: "two",
          displayCloseButton: true,
          multiColumnEnabled: true,
          closeButtonPosition: "right",
          closeOnOverlayClick: true,
        },
        schedule: {
          slots: [
            {
              id: "schedule-slot-1",
              endTime: "23:59",
              dayPreset: "everyday",
              startTime: "00:00",
            },
            {
              id: "schedule-slot-2",
              endTime: "23:59",
              dayPreset: "everyday",
              startTime: "00:00",
            },
          ],
          endDate: "",
          timeZone: "Asia/Kolkata",
          startDate: "",
          detailedScheduleEnabled: true,
        },
        triggers: {
          onExit: {
            enabled: false,
            triggerConditions: {
              enabled: false,
              delaySeconds: 0,
              pageViews: 0,
              operator: "and",
            },
            triggerOnScrollUp: false,
            triggerOnBackButton: false,
          },
          onClick: {
            enabled: false,
            clickAnchor: "#popup",
            triggerConditions: {
              enabled: false,
              delaySeconds: 0,
              pageViews: 0,
              operator: "and",
            },
          },
          onHover: {
            enabled: false,
            hoverElementId: "",
            triggerConditions: {
              enabled: false,
              delaySeconds: 0,
              pageViews: 0,
              operator: "and",
            },
          },
          onScroll: {
            enabled: false,
            scrollPercent: 50,
            triggerConditions: {
              enabled: false,
              delaySeconds: 0,
              pageViews: 0,
              operator: "and",
            },
          },
          onLanding: {
            enabled: true,
            triggerConditions: {
              enabled: false,
              delaySeconds: 0,
              pageViews: 0,
              operator: "and",
            },
          },
          onCustomEvent: {
            enabled: false,
            customEventName: "",
            triggerConditions: {
              enabled: false,
              delaySeconds: 0,
              pageViews: 0,
              operator: "and",
            },
          },
        },
        frequency: {
          showAgain: {
            mode: "session",
            intervalValue: 7,
            intervalUnit: "days",
          },
          stopShowing: {
            afterSignupOrClick: true,
            afterSeenEnabled: true,
            afterSeenCount: 5,
            afterClosedEnabled: true,
            afterClosedBehavior: "never_display_again",
          },
        },
      },
      width: 700,
      locale: "en",
      fontFamily: "Arial",
      backgroundColor: "#ffffff",
    },
    canvasPages: [
      {
        id: "f5902bc8-e71e-4b56-a590-349782c7b309",
        title: "Step 1",
        blocks: [
          {
            id: "dcf862dc-2ff6-45e1-8f43-07ebc2d5a88f",
            type: "section",
            styles: {
              margin: {
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              },
              padding: {
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              },
            },
            columns: "2",
            children: [
              [
                {
                  id: "b1353687-5d54-4c8c-8806-bf0a77de7aad",
                  url: "",
                  text: "GET 20% OFF",
                  type: "button",
                  styles: {
                    margin: {
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                    },
                    padding: {
                      top: 5,
                      left: 0,
                      right: 0,
                      bottom: 0,
                    },
                  },
                  fontSize: 13,
                  textColor: "#000000",
                  borderColor: "#FFCB54",
                  borderSides: "all",
                  borderWidth: 4,
                  borderRadius: 39,
                  buttonPadding: {
                    top: 12,
                    left: 24,
                    right: 24,
                    bottom: 12,
                  },
                  backgroundColor: "#ffffff",
                },
                {
                  id: "035f1574-3aff-4741-84cc-2d4b6e0d2fde",
                  type: "paragraph",
                  styles: {
                    margin: {
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                    },
                    padding: {
                      top: 10,
                      left: 10,
                      right: 10,
                      bottom: 10,
                    },
                  },
                  content:
                    '<p style="text-align: center;"><span style="color: rgb(27, 27, 32); font-family: Poppins; font-size: 20px; letter-spacing: normal;"><strong>You forgot something in your cart!</strong></span></p>',
                },
                {
                  id: "866f0ac7-b3df-4c62-b595-7d6adb975550",
                  type: "paragraph",
                  styles: {
                    margin: {
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                    },
                    padding: {
                      top: 10,
                      left: 10,
                      right: 10,
                      bottom: 10,
                    },
                  },
                  content:
                    '<p style="text-align: center; line-height: 1;"><span style="color: inherit; font-family: inherit; font-size: inherit; letter-spacing: normal;">Before you go, fill out your email for a </span></p><p style="text-align: center; line-height: 1;"><span style="color: inherit; font-family: inherit; font-size: inherit; letter-spacing: normal;"><strong>20% off code</strong> to use at checkout.</span></p>',
                },
                {
                  id: "27bab77f-14ac-4007-b4f8-14c8398b581c",
                  name: "Email",
                  type: "input",
                  label: "",
                  styles: {
                    margin: {
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                    },
                    padding: {
                      top: 0,
                      left: 10,
                      right: 10,
                      bottom: 0,
                    },
                  },
                  required: true,
                  inputType: "text",
                  inputWidth: "full",
                  labelColor: "#374151",
                  inputMargin: {
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                  },
                  labelMargin: {
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 8,
                  },
                  placeholder: "email@website.com",
                  defaultValue: "",
                  inputPadding: {
                    top: 10,
                    left: 12,
                    right: 12,
                    bottom: 10,
                  },
                  labelPadding: {
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                  },
                  inputFontSize: 15,
                  labelFontSize: 14,
                  inputTextColor: "#111827",
                  labelTextAlign: "left",
                  labelFontWeight: "normal",
                  inputBorderColor: "#d1d5db",
                  inputBorderSides: "all",
                  inputBorderWidth: 1,
                  inputBorderRadius: 6,
                  inputBackgroundColor: "#ffffff",
                  inputPlaceholderColor: "#9ca3af",
                },
                {
                  id: "046d68dd-e631-45a3-b729-a1c3276c08fc",
                  url: "",
                  text: "Get coupon",
                  type: "button",
                  styles: {
                    margin: {
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                    },
                    padding: {
                      top: 6,
                      left: 0,
                      right: 0,
                      bottom: 3,
                    },
                  },
                  fontSize: 15,
                  textColor: "#ffffff",
                  borderColor: "#000000",
                  borderSides: "all",
                  borderWidth: 0,
                  borderRadius: 30,
                  buttonPadding: {
                    top: 12,
                    left: 24,
                    right: 24,
                    bottom: 12,
                  },
                  backgroundColor: "#f5ba25",
                },
              ],
              [
                {
                  id: "36fef265-889e-4417-b29d-a275bf9b7483",
                  alt: "",
                  src: "https://cdn.wisepops.net/cdn-cgi/image/fit=scale-down,format=auto,width=1724/images-secure/popups/w.ZniQi6KSVq/01684a642ed5f99d268f97c9fc6a4338.png",
                  type: "image",
                  align: "center",
                  width: "full",
                  styles: {
                    margin: {
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                    },
                    padding: {
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                    },
                  },
                  visibility: {
                    mobile: false,
                    tablet: true,
                    desktop: true,
                  },
                },
              ],
            ],
          },
        ],
      },
    ],
    activeCanvasPageId: "f5902bc8-e71e-4b56-a590-349782c7b309",
  };
}
